import React, { useState } from 'react';
import { TrendingUp, Calculator, Lightbulb, Target, PiggyBank, Building, Shield } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

interface InvestmentCalculation {
  monthlyAmount: number;
  monthlyRate: number;
  months: number;
  totalInvested: number;
  totalValue: number;
  totalInterest: number;
  totalReturn: number;
}

const Investments: React.FC = () => {
  useSEO({
    title: 'Como Começar a Investir em 2026: Guia de Renda Fixa e Variável | O Teu Dinheiro',
    description: 'Aprenda a investir do zero com segurança: Tesouro Direto, CDB, LCI/LCA, Fundos Imobiliários (FIIs) e simulador de rentabilidade mensal.',
    keywords: 'como investir em 2026, investimentos para iniciantes, renda fixa, CDB, LCI, LCA, Tesouro Direto, simulador de investimentos',
    url: 'https://www.oteudinheiro.com.br/investimentos'
  });
  const [monthlyAmount, setMonthlyAmount] = useState<string>('');
  const [monthlyRate, setMonthlyRate] = useState<string>('0.5');
  const [months, setMonths] = useState<string>('12');
  const [calculation, setCalculation] = useState<InvestmentCalculation | null>(null);
  const [error, setError] = useState<string>('');

  const calculateInvestment = () => {
    const amount = parseFloat(monthlyAmount);
    const rate = parseFloat(monthlyRate);
    const period = parseInt(months);

    if (isNaN(amount) || isNaN(rate) || isNaN(period)) {
      setError('Por favor, preencha todos os campos com valores válidos.');
      setCalculation(null);
      return;
    }

    if (amount <= 0 || rate < 0 || period <= 0) {
      setError('Os valores devem ser maiores que zero.');
      setCalculation(null);
      return;
    }

    const monthlyRateDecimal = rate / 100;
    let totalInvested = 0;
    let totalValue = 0;

    for (let i = 1; i <= period; i++) {
      totalInvested += amount;
      totalValue = (totalValue + amount) * (1 + monthlyRateDecimal);
    }

    const totalInterest = totalValue - totalInvested;
    const totalReturn = ((totalValue / totalInvested - 1) * 100);

    setCalculation({
      monthlyAmount: amount,
      monthlyRate: rate,
      months: period,
      totalInvested,
      totalValue,
      totalInterest,
      totalReturn
    });

    setError('');
  };

  const investmentOptions = [
    {
      icon: <PiggyBank className="w-6 h-6" />,
      title: '💰 Poupança',
      advantages: ['Segura', 'Sem taxas', 'Fácil de resgatar'],
      disadvantages: ['Rendimento baixo (0,5% ao mês)']
    },
    {
      icon: <Building className="w-6 h-6" />,
      title: '📈 CDB',
      advantages: ['Rendimento melhor que poupança', 'Seguro'],
      disadvantages: ['Taxa de IOF se resgatar antes de 30 dias']
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: '🏦 Tesouro Direto',
      advantages: ['Rendimento atrativo', 'Governo federal'],
      disadvantages: ['Pode perder valor se resgatar antes do vencimento']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-emerald-50 section-padding">
        <div className="container-custom text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Investimentos para Iniciantes
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comece a investir com segurança e conhecimento. Aprenda sobre as 
            opções de renda fixa e como fazer seu dinheiro trabalhar para você.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Renda Fixa: Onde Começar
                </h2>
                <div className="space-y-6">
                  {investmentOptions.map((option, index) => (
                    <div key={index} className="card p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="text-2xl">{option.icon}</div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {option.title}
                        </h3>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-green-700 mb-2">✅ Vantagens:</h4>
                          <ul className="space-y-1">
                            {option.advantages.map((adv, idx) => (
                              <li key={idx} className="text-sm text-gray-600">• {adv}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-red-700 mb-2">❌ Desvantagens:</h4>
                          <ul className="space-y-1">
                            {option.disadvantages.map((dis, idx) => (
                              <li key={idx} className="text-sm text-gray-600">• {dis}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="tip-box">
                <div className="flex items-start space-x-3">
                  <Lightbulb className="w-6 h-6 text-secondary-600 mt-1" />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      🎯 Regra Básica
                    </h4>
                    <p className="text-gray-700">
                      Comece com pequenos valores e aumente gradualmente. É melhor 
                      investir R$ 100 por mês do que R$ 1.200 de uma vez. 
                      A constância é mais importante que o valor inicial.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Calculator */}
            <div className="card p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Calculadora de Investimento
                </h3>
                <p className="text-gray-600">
                  Simule investimentos mensais e veja o resultado
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valor Mensal (R$)
                  </label>
                  <input
                    type="number"
                    value={monthlyAmount}
                    onChange={(e) => setMonthlyAmount(e.target.value)}
                    placeholder="100,00"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Taxa Mensal (%)
                  </label>
                  <input
                    type="number"
                    value={monthlyRate}
                    onChange={(e) => setMonthlyRate(e.target.value)}
                    placeholder="0,5"
                    step="0.1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tempo (meses)
                  </label>
                  <input
                    type="number"
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                    placeholder="12"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <button
                  onClick={calculateInvestment}
                  className="w-full btn btn-primary bg-green-600 hover:bg-green-700 focus:ring-green-500"
                >
                  Calcular
                </button>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                {calculation && (
                  <div className="p-6 bg-gray-50 rounded-lg space-y-3">
                    <h4 className="font-semibold text-gray-900 text-center mb-4">
                      Resultado do Investimento
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="text-gray-600">Total Investido:</div>
                      <div className="font-medium">R$ {calculation.totalInvested.toFixed(2)}</div>
                      
                      <div className="text-gray-600">Total com Juros:</div>
                      <div className="font-medium text-green-600">R$ {calculation.totalValue.toFixed(2)}</div>
                      
                      <div className="text-gray-600">Juros Ganhos:</div>
                      <div className="font-medium text-green-600">R$ {calculation.totalInterest.toFixed(2)}</div>
                      
                      <div className="text-gray-600">Retorno Total:</div>
                      <div className="font-bold text-green-700">{calculation.totalReturn.toFixed(2)}%</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Tips Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Dicas para Investir com Segurança
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Aprenda os princípios fundamentais para começar a investir 
              de forma responsável e segura.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Defina seus Objetivos
              </h3>
              <p className="text-gray-600 text-sm">
                Antes de investir, saiba para que você está economizando: 
                aposentadoria, casa própria, viagem, etc.
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Comece Pequeno
              </h3>
              <p className="text-gray-600 text-sm">
                Não precisa de muito dinheiro para começar. 
                R$ 50 ou R$ 100 por mês já é um excelente começo.
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Diversifique
              </h3>
              <p className="text-gray-600 text-sm">
                Não coloque todo seu dinheiro em um só lugar. 
                Distribua entre diferentes tipos de investimento.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Investments;
